import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { chevronBackOutline, chevronDownOutline, chevronForwardOutline } from "ionicons/icons";
import { DAY_STATE_PRIORITY, type DayState } from "@/lib/discover-day-state";
import {
  type DayKey,
  type YearMonth,
  firstWeekdayOfMonth,
  lastDayOfMonth,
  monthDays,
  monthOf,
  shiftDays,
  shiftMonths,
  weekOf,
} from "@/lib/discover-window";
import { cn } from "@/lib/style/cva.config";
import { dayKeyToDate, formatInTz } from "@/utils/format-datetime";

/** De drie die je op elk scherm kunt tegenkomen, en die de legenda dus altijd uitlegt. */
const ALWAYS_KEYED: readonly DayState[] = ["scheduled", "applied", "available"];

type DiscoverCalendarProps = {
  /** The day the calendar is standing on — the day the list is showing at the top. */
  anchor: DayKey;
  /** Take the list to a day. The list keeps every other day; only the scroll position moves. */
  onJump: (day: DayKey) => void;
  /** What is going on per day: yours first, open work otherwise. See `discover-day-state.ts`. */
  states: ReadonlyMap<DayKey, DayState>;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Today, so the past can be dimmed and "today" can be pointed at. */
  today: DayKey;
};

/**
 * The calendar you page through, replacing the four period chips.
 *
 * Two things keep it from becoming the screen instead of the way into it:
 *
 * 1. **It opens collapsed**, as a single week above the list. Being able to see eight rows at once is
 *    why Ontdek became a line instead of a poster; a grid that fills half the screen by default
 *    undoes that. Opening the month is the reader's choice, never the starting position.
 * 2. **It moves the list, it does not cut it.** Tapping a day scrolls the list to that day and leaves
 *    everything else in place, so a tap can never strand you on "niets gevonden" and there is
 *    nothing to undo afterwards. It used to narrow the list to one day, which meant every tap
 *    demanded a second one — on "Bekijk de hele maand" — to get back out.
 *
 * The arrows move at the granularity you can see: a week while collapsed, a month while open.
 * Anything else asks the reader to hold a mental model of a control they are not looking at.
 */
export function DiscoverCalendar({
  anchor,
  onJump,
  states,
  expanded,
  onExpandedChange,
  today,
}: DiscoverCalendarProps) {
  const { t } = useTranslation();

  const month = monthOf(anchor);
  const days = expanded ? monthDays(month) : weekOf(anchor);
  const leadingBlanks = expanded ? firstWeekdayOfMonth(month) : 0;

  // Stepping back is only blocked where there is nothing to go back to: days already past hold no
  // work you can still apply for, so a step is allowed as long as it still ends in the future.
  const previous = expanded ? shiftMonths(anchor, -1) : shiftDays(anchor, -7);
  const lastDayBack = expanded ? lastDayOfMonth(monthOf(previous)) : weekOf(previous)[6];
  const canGoBack = lastDayBack >= today;

  const step = (to: DayKey) => onJump(clampToToday(to, today));

  // Alleen zodra de maand open is: een vaste legenda onder een strookje van één regel kost meer
  // hoogte dan het strookje zelf.
  //
  // Ingepland, aangemeld en werk open staan er altijd bij. Dat waren eerst ook alleen de kleuren
  // die op dat moment in beeld stonden, en dan verdween "ingepland" in elke maand waarin je
  // toevallig geen dienst had -- precies de maand waarin je je afvraagt wat groen ook alweer
  // betekende. De andere drie komen wél alleen als ze er zijn: een uitnodiging of een
  // afmeldverzoek is zeldzaam genoeg dat een uitleg zonder aanleiding meer vraagt dan hij
  // oplevert.
  const legend = expanded
    ? DAY_STATE_PRIORITY.filter(
        (state) => ALWAYS_KEYED.includes(state) || days.some((day) => states.get(day) === state),
      )
    : [];

  return (
    <div className="flex flex-col gap-2 pb-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onExpandedChange(!expanded)}
          className="touch-target -ml-1 inline-flex items-center gap-1 rounded-lg px-1 py-1 transition-opacity active:opacity-70"
          aria-expanded={expanded}
        >
          <span className="text-md font-semibold capitalize text-txt-primary">
            {monthLabel(month)}
          </span>
          <IonIcon
            icon={chevronDownOutline}
            aria-hidden="true"
            className={cn(
              "size-4 text-txt-secondary transition-transform",
              expanded && "rotate-180",
            )}
          />
        </button>

        <div className="flex items-center gap-1">
          <StepButton
            icon={chevronBackOutline}
            label={t("common:discover.calendar.previous")}
            disabled={!canGoBack}
            onClick={() => step(previous)}
          />
          <StepButton
            icon={chevronForwardOutline}
            label={t("common:discover.calendar.next")}
            onClick={() => step(expanded ? shiftMonths(anchor, 1) : shiftDays(anchor, 7))}
          />
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {/* All seven, in both states: `weekOf` starts on Monday just as the month grid does, so
            each initial heads its own column either way. Hiding six of them with `sr-only` would
            take them out of flow — `sr-only` is `position: absolute` — leaving one header plus
            seven days in a seven-column grid, which pushes the strip a column over and wraps
            Sunday onto its own row. */}
        {weekdayInitials().map((weekday) => (
          <span
            key={weekday}
            aria-hidden="true"
            className="pb-1 text-center text-xsm font-medium lowercase text-txt-tertiary"
          >
            {weekday}
          </span>
        ))}

        {Array.from({ length: leadingBlanks }, (_, index) => (
          <span key={`blank-${index}`} aria-hidden="true" />
        ))}

        {days.map((day) => (
          <DayCell
            key={day}
            day={day}
            state={states.get(day)}
            isAnchored={day === anchor}
            isToday={day === today}
            isOutsideMonth={expanded ? false : monthOf(day).month !== month.month}
            isPast={day < today}
            onClick={() => {
              // Picking a day out of the open month closes it. The calendar is pinned to the top of
              // the list, and a pinned six-row grid would cover half of what you just jumped to see.
              // The month is a picker you open on purpose; picking is what closes it. The arrows do
              // not — stepping month by month is browsing inside the picker, not choosing.
              onExpandedChange(false);
              onJump(day);
            }}
          />
        ))}
      </div>

      {legend.length > 0 && (
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
          {legend.map((state) => (
            <li key={state} className="flex items-center gap-1.5 text-xsm text-txt-tertiary">
              <DayMark state={state} />
              {t(`common:discover.calendar.state.${state}`)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * What each state looks like on a five-pixel mark.
 *
 * Colour is spent on the states that are _yours_. "Work is open here" is the ordinary case on this
 * screen — most days have some — so it gets the quiet grey mark: if every day were the brand
 * colour, the colour would say nothing, and the days where something is actually going on for you
 * would have to shout over them.
 *
 * Green, red and amber mean here exactly what they mean on the status badges in Diensten: settled,
 * waiting on you, waiting on them.
 */
const TONES: Record<DayState, string> = {
  scheduled: "bg-success-100",
  cancellation: "bg-warning-100",
  applied: "bg-brand-500",
  available: "bg-txt-tertiary",
  cancelled: "bg-txt-tertiary",
};

/**
 * `cancelled` is a dash rather than a dot.
 *
 * It is the one state that is over rather than pending, and the palette has no sixth hue to give it
 * — the brand ramps carry three semantic colours and nothing else. Shape does the work colour
 * cannot, and a struck-through line is the right shape for "this came off".
 */
function DayMark({ state, inverted = false }: { state: DayState; inverted?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "rounded-full",
        state === "cancelled" ? "h-[3px] w-2.5" : state === "available" ? "size-1" : "size-1.5",
        inverted ? "bg-txt-inverted" : TONES[state],
      )}
    />
  );
}

function DayCell({
  day,
  state,
  isAnchored,
  isToday,
  isOutsideMonth,
  isPast,
  onClick,
}: {
  day: DayKey;
  state: DayState | undefined;
  isAnchored: boolean;
  isToday: boolean;
  isOutsideMonth: boolean;
  isPast: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const number = Number(day.split("-")[2]);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isToday ? "date" : undefined}
      // The colour of a five-pixel dot is not something a test can read, and it is not something a
      // screen reader reads either — the label below carries it in words, this carries it in one.
      data-state={state}
      aria-label={
        state
          ? t("common:discover.calendar.day", {
              date: fullDayLabel(day),
              state: t(`common:discover.calendar.state.${state}`),
            })
          : fullDayLabel(day)
      }
      className={cn(
        "flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg text-15 tabular-nums transition-opacity active:opacity-70",
        state ? "font-semibold text-txt-primary" : "text-txt-tertiary",
        (isPast || isOutsideMonth) && !isAnchored && "opacity-40",
        isAnchored && "bg-txt-primary text-txt-inverted",
        !isAnchored && isToday && "ring-1 ring-inset ring-bordercolor-primary",
      )}
    >
      <span aria-hidden="true">{number}</span>
      {/* An empty span of the same height on the days without a mark: the numbers have to sit on one
          line across the row, and a mark that appears and disappears would otherwise shift them. */}
      {state ? <DayMark state={state} inverted={isAnchored} /> : <span className="size-1.5" />}
    </button>
  );
}

function StepButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="touch-target rounded-lg p-1.5 transition-opacity active:opacity-70 disabled:opacity-30"
    >
      <IonIcon icon={icon} aria-hidden="true" className="size-5 text-txt-primary" />
    </button>
  );
}

/**
 * Monday to Sunday, in the reader's language.
 *
 * Derived rather than written down, because a hardcoded `["ma", "di", …]` here would be the very
 * thing this branch takes out of thirty-nine other call sites. 5 January 1970 was a Monday; the
 * date itself never shows, only the seven names it yields.
 */
function weekdayInitials(): string[] {
  // Expliciet in UTC gelezen, want de datum wordt hier ook in UTC gebouwd: elke andere zone
  // schuift 5 januari 1970 naar de 4e en zet de hele rij een dag terug.
  return Array.from({ length: 7 }, (_, index) =>
    formatInTz(Date.UTC(1970, 0, 5 + index), { weekday: "short", timeZone: "UTC" }),
  );
}

function monthLabel({ year, month }: YearMonth): string {
  // Ook UTC, om dezelfde reden: `Date.UTC(...)` erin, dus UTC eruit.
  return formatInTz(Date.UTC(year, month, 1), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "donderdag 14 augustus" — for the label a screen reader reads, where the bare number is useless. */
function fullDayLabel(day: DayKey): string {
  // Hier stond middernacht plus twaalf uur, omdat een kale dagsleutel westelijk van Greenwich een
  // dag terugvalt. `dayKeyToDate` rekent de echte middernacht in projecttijd uit en heeft die truc
  // niet nodig.
  return formatInTz(dayKeyToDate(day), {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function clampToToday(day: DayKey, today: DayKey): DayKey {
  return day < today ? today : day;
}
